<template>
  <div class="attachment-preview-wrapper">
    <!-- Image Attachment Preview -->
    <div v-if="['image', 'sticker'].includes(attachmentType)" class="image-preview-container">
      <img :src="sanitizeHardLinkUrl(attachment)" :alt="attachmentName || 'Image'" class="thumbnail-image" @click="openImagePreview" />
      <div class="media-action-bar">
        <button class="media-action-btn" @click="openImagePreview">
          <VIcon name="eye-line"></VIcon> Preview
        </button>
        <button class="media-action-btn" @click="downloadFile(attachment, attachmentName)">
          <VIcon name="download-line"></VIcon> Download
        </button>
      </div>
    </div>

    <!-- Video Attachment Preview -->
    <div v-else-if="attachmentType === 'video'" class="video-preview-container">
      <div v-if="isBrowserPlayableVideoUrl(attachment)" class="video-thumbnail-wrapper" @click="openVideoPreview">
        <!-- preload=metadata loads only enough to show the first frame as poster -->
        <video class="video-thumbnail" preload="metadata" muted :src="sanitizeHardLinkUrl(attachment)">
          <source :src="sanitizeHardLinkUrl(attachment)" :type="getVideoMimeType(attachment)" />
        </video>
        <div class="video-play-badge">
          <div class="play-circle">
            <div class="play-triangle"></div>
          </div>
        </div>
      </div>
      <div v-else class="videoFallback">
        <VideoFallback :url="sanitizeHardLinkUrl(attachment)" />
      </div>
      <div class="media-action-bar">
        <button v-if="isBrowserPlayableVideoUrl(attachment)" class="media-action-btn" @click="openVideoPreview">
          <VIcon name="eye-line"></VIcon> Preview
        </button>
        <button class="media-action-btn" @click="downloadFile(attachment, attachmentName)">
          <VIcon name="download-line"></VIcon> Download
        </button>
      </div>
    </div>

    <!-- Document Attachment Preview -->
    <div v-else-if="['document', 'file'].includes(attachmentType)" class="document-preview-container">
      <div class="document-card">
        <div class="document-icon-wrapper">
          <img src="@/resources/images/document_outline.svg" alt="Document" class="document-icon" />
        </div>
        <div class="document-name" :title="attachmentName || 'Document'">
          {{ attachmentName || 'Document' }}
        </div>
      </div>
      <div class="media-action-bar">
        <button class="media-action-btn" @click="openDocumentPreview">
          <VIcon name="eye-line"></VIcon> Preview
        </button>
        <button class="media-action-btn" @click="downloadFile(attachment, attachmentName)">
          <VIcon name="download-line"></VIcon> Download
        </button>
      </div>
    </div>

    <!-- Video Preview Modal -->
    <ion-modal
      :is-open="isVideoPreviewOpen"
      @didDismiss="closeVideoPreview"
      class="attachment-preview-modal"
    >
      <ion-header>
        <ion-toolbar>
          <ion-title>Video Preview</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeVideoPreview">
              <VIcon name="close-line"></VIcon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="preview-modal-content">
        <div class="video-preview-content">
          <video class="preview-video" controls controlslist="nodownload" autoplay>
            <source :src="sanitizeHardLinkUrl(attachment)" :type="getVideoMimeType(attachment)" />
          </video>
          <div class="preview-actions">
            <button class="download-link" @click="downloadFile(attachment, attachmentName)">
              <VIcon name="download-line"></VIcon>
              Download
            </button>
          </div>
        </div>
      </ion-content>
    </ion-modal>

    <!-- Image Preview Modal -->
    <ion-modal 
      :is-open="isImagePreviewOpen"
      @didDismiss="closeImagePreview"
      class="attachment-preview-modal"
    >
      <ion-header>
        <ion-toolbar>
          <ion-title>Image Preview</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeImagePreview">
              <VIcon name="close-line"></VIcon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="preview-modal-content">
        <div class="image-preview-content">
          <img :src="sanitizeHardLinkUrl(attachment)" :alt="attachmentName || 'Image'" class="preview-image" />
          <div class="preview-actions">
            <button class="download-link" @click="downloadFile(attachment, attachmentName)">
              <VIcon name="download-line"></VIcon>
              Download
            </button>
          </div>
        </div>
      </ion-content>
    </ion-modal>

    <!-- Document Preview Modal -->
    <ion-modal 
      :is-open="isDocumentPreviewOpen"
      @didDismiss="closeDocumentPreview"
      class="attachment-preview-modal"
    >
      <ion-header>
        <ion-toolbar>
          <ion-title>Document Preview</ion-title>
          <ion-buttons slot="end">
            <ion-button @click="closeDocumentPreview">
              <VIcon name="close-line"></VIcon>
            </ion-button>
          </ion-buttons>
        </ion-toolbar>
      </ion-header>
      <ion-content class="preview-modal-content">
        <div class="document-preview-content">
          <div v-if="isPdfOrOfficeDocument" class="document-preview-unavailable">
            <img src="@/resources/images/document_outline.svg" alt="Document" class="document-icon-large" />
            <p class="document-name-large">{{ attachmentName || 'Document' }}</p>
            <p class="preview-note">Preview not available for this file type</p>
            <button class="download-link" @click="downloadFile(attachment, attachmentName)">
              <VIcon name="download-line"></VIcon>
              Download
            </button>
          </div>
          <div v-else class="document-preview-iframe-wrapper">
            <iframe 
              :src="`https://docs.google.com/gview?url=${encodeURIComponent(sanitizeHardLinkUrl(attachment))}&embedded=true`"
              class="document-iframe"
              allow="fullscreen"
            ></iframe>
          </div>
        </div>
      </ion-content>
    </ion-modal>
  </div>
</template>

<script>
import { IonButton, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonContent } from '@ionic/vue';
import { sanitizeHardLinkUrl } from '@/helpers/message.helpers';
import { getVideoMimeType, isBrowserPlayableVideoUrl } from '@/helpers/video.helpers';
import VideoFallback from '@/components/VideoFallback.vue';

export default {
  components: {
    IonButton,
    IonModal,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButtons,
    IonContent,
    VideoFallback,
  },
  props: {
    message: { type: Object, default: null },
    // direct-URL alternatives for agent/bot messages
    url:      { type: String, default: null },
    fileType: { type: String, default: null },
    fileName: { type: String, default: null },
  },
  data() {
    return {
      isImagePreviewOpen: false,
      isVideoPreviewOpen: false,
      isDocumentPreviewOpen: false,
      sanitizeHardLinkUrl,
    };
  },
  computed: {
    attachment() {
      return this.url || this.message?.template?.attachment;
    },
    attachmentName() {
      return this.fileName || this.message?.template?.attachmentname;
    },
    attachmentType() {
      return this.fileType || this.message?.template?.attachmenttype;
    },
    isPdfOrOfficeDocument() {
      const url = this.attachment || '';
      const extension = url.split('.').pop()?.toLowerCase() || '';
      return ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(extension);
    },
  },
  methods: {
    openImagePreview() {
      this.isImagePreviewOpen = true;
    },
    getVideoMimeType,
    isBrowserPlayableVideoUrl,
    closeImagePreview() {
      this.isImagePreviewOpen = false;
    },
    openVideoPreview() {
      this.isVideoPreviewOpen = true;
    },
    closeVideoPreview() {
      this.isVideoPreviewOpen = false;
    },
    openDocumentPreview() {
      this.isDocumentPreviewOpen = true;
    },
    closeDocumentPreview() {
      this.isDocumentPreviewOpen = false;
    },
    async downloadFile(url, name) {
      try {
        const sanitized = sanitizeHardLinkUrl(url);
        const res = await fetch(sanitized);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = name || sanitized.split('/').pop().split('?')[0] || 'image';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      } catch {
        window.open(sanitizeHardLinkUrl(url), '_blank', 'noopener,noreferrer');
      }
    },
  },
};
</script>

<style scoped>
/* Inline-block so the parent's text-align: right pushes it to the agent side */
.attachment-preview-wrapper {
  display: inline-block;
  text-align: left;
  vertical-align: top;
  max-width: 300px;
}

/* Image Preview Container */
.image-preview-container {
  margin: 8px 0;
}

.preview-thumbnail {
  position: relative;
  display: inline-block;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  max-width: 100%;
}

.thumbnail-image {
  max-width: 240px;
  max-height: 300px;
  width: auto;
  height: auto;
  display: block;
  border-radius: 8px 8px 0 0;
  cursor: pointer;
}

.media-action-bar {
  display: flex;
  gap: 6px;
  padding: 6px 4px 4px;
}

/* Connect document action bar to the card above it */
.document-preview-container .media-action-bar {
  border: 1px solid var(--ion-color-medium, #d3d3d3);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: 6px 8px 8px;
  background-color: var(--ion-color-light, #f8f8f8);
}

.media-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid var(--ion-color-medium, #999);
  border-radius: 6px;
  background: var(--ion-color-light, #f4f4f4);
  color: var(--ion-color-dark, #222);
  cursor: pointer;
  transition: background 0.15s ease;
}

.media-action-btn:hover {
  background: var(--ion-color-light-shade, #e4e4e4);
}

/* Video Preview Container */
.video-preview-container {
  margin: 8px 0;
}

.video-thumbnail-wrapper {
  position: relative;
  display: inline-block;
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  line-height: 0;
}

.video-thumbnail {
  display: block;
  width: 240px;
  max-width: 100%;
  height: auto;
  max-height: 180px;
  object-fit: cover;
  border-radius: 8px;
}

.video-play-badge {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  border-radius: 8px;
  pointer-events: none;
}

.play-circle {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.play-triangle {
  width: 0;
  height: 0;
  margin-left: 4px;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 16px solid #333;
}

.video-preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.preview-video {
  width: 100%;
  max-height: 70vh;
  border-radius: 8px;
}

/* Document Preview Container */
.document-preview-container {
  margin: 8px 0;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 6px;
  border: 1px solid var(--ion-color-medium, #d3d3d3);
  border-radius: 8px 8px 0 0;
  background-color: var(--ion-color-light, #f8f8f8);
}

.document-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  min-height: 36px;
  background-color: var(--ion-color-primary, #007aff);
  border-radius: 6px;
  padding: 4px;
  flex-shrink: 0;
}

.document-icon {
  width: 24px;
  height: 24px;
  filter: invert(1);
}

.document-name {
  font-weight: 500;
  font-size: 13px;
  color: var(--ion-color-dark, #222);
  word-break: break-word;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Modal Styles */
.attachment-preview-modal {
  --background: var(--ion-color-light, #f8f8f8);
}

.preview-modal-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

/* Image Preview Content */
.image-preview-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.preview-image {
  max-width: 100%;
  max-height: 70vh;
  width: auto;
  height: auto;
  border-radius: 8px;
}

.preview-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.download-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background-color: var(--ion-color-success, #10b981);
  color: white;
  border: none;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.download-link:hover {
  background-color: var(--ion-color-success-shade, #059669);
}

/* Document Preview Content */
.document-preview-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 16px;
}

.document-preview-unavailable {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  text-align: center;
  padding: 32px 16px;
}

.document-icon-large {
  width: 80px;
  height: 80px;
  opacity: 0.5;
}

.document-name-large {
  font-size: 16px;
  font-weight: 500;
  color: var(--ion-color-dark, #222);
  word-break: break-word;
  margin: 0;
}

.preview-note {
  font-size: 14px;
  color: var(--ion-color-medium, #999);
  margin: 0;
}

.document-preview-iframe-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-iframe {
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 8px;
  min-height: 500px;
}

/* Responsive Design */
@media (max-width: 600px) {
  .document-card {
    flex-direction: column;
    padding: 12px;
  }

  .document-icon-wrapper {
    width: 100%;
  }

  .thumbnail-image {
    max-width: 100%;
    max-height: 200px;
  }

  .preview-image {
    max-height: 50vh;
  }

  .action-button {
    flex: 1;
  }

  .document-actions {
    width: 100%;
  }

  .download-link {
    width: 100%;
    justify-content: center;
  }
}
</style>
